import clsx from 'clsx';
import type { ReactNode } from 'react';

import { Icon } from './Icon';
import styles from './DataTable.module.css';

export interface Column<Row, SortKey extends string = string> {
  /** Stable identity for React keys and sort state. */
  id: string;
  header: ReactNode;
  /** Set to make the header a sort control. */
  sortKey?: SortKey;
  align?: 'left' | 'right';
  width?: string;
  /** Hidden below this viewport width, so the table degrades instead of scrolling forever. */
  hideBelow?: 'sm' | 'md' | 'lg';
  render: (row: Row) => ReactNode;
}

export type SortDirection = 'asc' | 'desc';

export interface DataTableProps<Row, SortKey extends string = string> {
  rows: readonly Row[];
  columns: readonly Column<Row, SortKey>[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  sort?: { key: SortKey; direction: SortDirection };
  onSortChange?: (key: SortKey) => void;
  caption?: string;
  className?: string;
}

export function DataTable<Row, SortKey extends string = string>({
  rows,
  columns,
  rowKey,
  onRowClick,
  sort,
  onSortChange,
  caption,
  className,
}: DataTableProps<Row, SortKey>) {
  return (
    <div className={clsx(styles.scroller, className)}>
      <table className={styles.table}>
        {caption ? <caption className="u-visually-hidden">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => {
              const isSorted = sort !== undefined && column.sortKey === sort.key;
              const sortable = column.sortKey !== undefined && onSortChange !== undefined;

              return (
                <th
                  key={column.id}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  aria-sort={isSorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={clsx(
                    sortable && styles.sortable,
                    column.align === 'right' && styles.alignRight,
                    column.hideBelow && `hide-below-${column.hideBelow}`,
                  )}
                  onClick={
                    sortable
                      ? () => {
                          if (column.sortKey) onSortChange(column.sortKey);
                        }
                      : undefined
                  }
                >
                  <span className={styles.headInner}>
                    {column.header}
                    {sortable ? (
                      <Icon
                        name={isSorted && sort.direction === 'asc' ? 'chevron-up' : 'chevron-down'}
                        size={12}
                        className={clsx(styles.sortIcon, isSorted && styles.sortActive)}
                      />
                    ) : null}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={clsx(styles.row, onRowClick && styles.clickable)}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
            >
              {columns.map((column) => (
                <td
                  key={column.id}
                  className={clsx(
                    column.align === 'right' && styles.numeric,
                    column.hideBelow && `hide-below-${column.hideBelow}`,
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
